import { Typography } from '@mui/material';

import IconButton from 'src/components/IconButton';
import { DYDX_TOKEN } from 'src/constants/tokens';
import { useDelegationSWR } from 'src/hooks/SWR/cosmos/useDelegationSWR';
import { useCurrentAccount } from 'src/hooks/useAccount';
import type { Validator } from 'src/types/validator';
import { fix, toDisplayDenomAmount } from 'src/utils/number';
import { isEqualsIgnoringCase } from 'src/utils/string';

import ValidatorItem from './components/ValidatorItem';
import {
  Container,
  Divider,
  EmptyIconContainer,
  EmptyListContainer,
  Header,
  HeaderTitle,
  StyledBottomSheet,
  ValidatorContainer,
  ValidatorList,
} from './styled';

import { ReactComponent as CloseIcon } from 'src/assets/svg/Close.svg';
import { ReactComponent as EmptyValidatorsIcon } from 'src/assets/svg/TxTrackingFail.svg';

type ValidatorBottomSheetProps = Omit<React.ComponentProps<typeof StyledBottomSheet>, 'children'> & {
  validatorList: Validator[];
  rightText: string;
  onClickValidator?: (validatorInfo: Validator) => void;
};

export default function ValidatorBottomSheet({ validatorList, rightText, onClickValidator, onClose, ...remainder }: ValidatorBottomSheetProps) {
  const { currentAccount } = useCurrentAccount();

  const delegation = useDelegationSWR({ cosmosAddress: currentAccount.cosmosAddress });

  return (
    <StyledBottomSheet
      {...remainder}
      onClose={() => {
        onClose?.({}, 'backdropClick');
      }}
    >
      <Container>
        <Header>
          <HeaderTitle>
            <Typography variant="h2">Select Validator</Typography>
          </HeaderTitle>
          <IconButton
            onClick={() => {
              onClose?.({}, 'backdropClick');
            }}
          >
            <CloseIcon />
          </IconButton>
        </Header>
        {validatorList.length ? (
          <ValidatorContainer>
            <ValidatorList>
              {validatorList.map((item) => {
                const rightValue =
                  rightText === 'Staked'
                    ? fix(
                        toDisplayDenomAmount(
                          delegation.data?.delegation_responses?.find((validator) =>
                            isEqualsIgnoringCase(validator.delegation.validator_address, item.operator_address),
                          )?.balance.amount || '0',
                          DYDX_TOKEN.decimals,
                        ),
                        6,
                      )
                    : '';
                return (
                  <ValidatorItem
                    key={item.operator_address}
                    rightText={rightText}
                    rightValue={rightValue}
                    validatorInfo={item}
                    onClick={(validatorInfo) => {
                      onClickValidator?.(validatorInfo);
                    }}
                  />
                );
              })}
            </ValidatorList>
            <Divider />
          </ValidatorContainer>
        ) : (
          <EmptyListContainer>
            <EmptyIconContainer>
              <EmptyValidatorsIcon />
            </EmptyIconContainer>
            <Typography variant="h4">No available validators to select</Typography>
          </EmptyListContainer>
        )}
      </Container>
    </StyledBottomSheet>
  );
}
